import React from 'react';
import { API_BASE_URL } from '../../services/api';
import AllocationBarChart from './charts/AllocationDonutChart';

// Default key facts used when no custom content is available
export const DEFAULT_KEY_FACTS = [
  { label: "Investment Manager", value: "Drummond Capital Partners Pty Ltd" },
  { label: "Asset Class", value: "Global Multi Asset (Diversified)" },
  { label: "Investment Style", value: "Active" },
  { label: "Benchmark Index", value: "FE AMI Mixed Asset \u2013 Growth" },
  { label: "Growth exposure range", value: "60-80%" },
  { label: "Number Holdings", value: "\u2014" },
  { label: "Yield (SAA estimate)", value: "\u2014" },
  { label: "Inception Date", value: "1st November 2019" },
  { label: "Minimum Investment", value: "$25,000" },
  { label: "Minimum Investment Horizon", value: "7 years" },
  { label: "Investment Management Fee", value: "0.2574%" },
  { label: "Indirect Costs (net of rebates)", value: "0.38%" },
];

export const DEFAULT_KEY_FACTS_FOOTNOTES = "";

// Key Fact Row Component - supports multi-line values via \n
const KeyFactRow = ({ label, value }) => {
  const renderValue = () => {
    if (typeof value === 'string' && value.includes('\n')) {
      return value.split('\n').map((line, i) => (
        <div key={i} className="key-fact-value">{line}</div>
      ));
    }
    return <div className="key-fact-value">{value}</div>;
  };

  return (
    <div className="key-fact-row">
      <div className="key-fact-label">{label}</div>
      {renderValue()}
    </div>
  );
};

// Render text with paragraph support — double newline creates a new <p>, single newline is a <br>
const TextWithParagraphs = ({ text, className }) => {
  if (!text) return null;
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, i) => (
    <p key={i} className={className}>
      {para.split('\n').map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
};

const FactSheetPage1 = ({ data, theme, chartRefs }) => {
  const { portfolio, content, branding } = data;
  // If branding exists (branded portfolio), use brand logo or nothing; otherwise Drummond default
  const rawLogoUrl = branding ? branding.logo_url : (theme.logo_url || '/logo-name.png');
  // Resolve relative API paths to absolute URLs (needed when frontend/backend are on different ports)
  const logoUrl = rawLogoUrl && rawLogoUrl.startsWith('/api') ? `${API_BASE_URL}${rawLogoUrl}` : rawLogoUrl;
  // Use custom key facts from content if available, otherwise defaults
  const rawKeyFacts = content?.key_facts || DEFAULT_KEY_FACTS;
  // Inject auto-calculated holdings count from backend
  const keyFacts = rawKeyFacts.map(fact =>
    fact.label === 'Number Holdings' && data.holdings_count != null
      ? { ...fact, value: String(data.holdings_count) }
      : fact
  );
  const keyFactsFootnotes = content?.key_facts_footnotes || DEFAULT_KEY_FACTS_FOOTNOTES;
  // Nullish (not ||) so an intentionally cleared (empty) title stays hidden
  const wholesaleTitle = content?.wholesale_title ?? 'Wholesale clients only';
  // Notice text from Admin > Disclaimers, resolved server-side ('' = hidden on this fact sheet)
  const wholesaleText = content?.wholesale_text_resolved ?? '[Wholesale notice not resolved]';

  return (
    <div className="fact-sheet-page page-1">
      {/* Header */}
      <header className="fs-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="header-title">{portfolio.name}</h1>
            <p className="header-subtitle">FACT SHEET</p>
          </div>
          <div className="header-right">
            {logoUrl && (
              <div className="logo">
                <img src={logoUrl} alt="Logo" />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="fs-content">
        {/* Quote Section */}
        <section className="quote-section">
          <p className="quote-text">"{content?.quote_text || 'Our detailed research leads us to actionable investment insights. Filtering through the noise, we stay focused on what matters and transparent in our thinking.'}"</p>

        </section>

        {/* Intro Section */}
        <section className="intro-section">
          <TextWithParagraphs
            text={content?.intro_text || 'This portfolio has a neutral exposure to growth assets such as Australian and global equities. The portfolio has exposure to fixed interest and real assets and a small allocation to alternative assets including hedge funds and private equity.'}
            className="intro-text"
          />
        </section>

        {/* Two Column Layout */}
        <div className="two-column">
          {/* Left Column */}
          <div className="left-column">
            {/* Editable left-column sections (1-3 {header, body}); the backend resolves
                legacy records into the same list, matching the PDF template */}
            {(content?.left_sections || []).map((s, i) => (
              <div className="section-box" key={i}>
                <h2 className="section-heading">{s.header}</h2>
                <TextWithParagraphs text={s.body} className="section-text" />
              </div>
            ))}

            {/* Neutral Asset Allocation - use SAA allocation (neutral), fall back to actual allocation */}
            {(data.saa_allocation || data.allocation) && (data.saa_allocation || data.allocation).length > 0 && (
              <div className="asset-allocation-section">
                <h2 className="allocation-heading">NEUTRAL ASSET ALLOCATION</h2>
                <p className="allocation-subheading">{data.neutral_growth_pct || 70}% GROWTH ASSETS & {100 - (data.neutral_growth_pct || 70)}% DEFENSIVE ASSETS</p>
                <div className="chart-placeholder">
                  <AllocationBarChart ref={chartRefs?.allocation || null} allocation={data.saa_allocation || data.allocation} />
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Key Facts */}
          <div className="right-column">
            <div className="key-facts">
              <h3 className="key-facts-heading">KEY FACTS</h3>

              {keyFacts.map((fact, i) => (
                <KeyFactRow key={i} label={fact.label} value={fact.value} />
              ))}

              {keyFactsFootnotes && (
                <div className="key-fact-note">
                  {keyFactsFootnotes.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < keyFactsFootnotes.split('\n').length - 1 && <br/>}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wholesale disclaimer */}
      {(wholesaleTitle.trim() || wholesaleText.trim()) && (
        <div className="fs-wholesale-disclaimer">
          {wholesaleTitle.trim() && <p className="wholesale-title">{wholesaleTitle}</p>}
          {wholesaleText.trim() && <p className="wholesale-text">{wholesaleText}</p>}
        </div>
      )}

      {/* Footer */}
      <footer className="fs-footer">
        <div className="footer-left">DRUMMOND CAPITAL PARTNERS</div>
        <div className="footer-right">PAGE 1</div>
      </footer>
    </div>
  );
};

export default FactSheetPage1;
